import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { ROUTES } from '@/constants/routes'

import { useProductConfig } from '@/contexts/ProductConfigContext'

import useFetch from '@/hooks/useFetch'

import { isAuthRoute, normalizePath, shouldSaveAuthRedirect } from '@/utils/authRoutes'
import { applyRedirect, resolvePostLoginRedirect } from '@/utils/navigation'

import { PRODUCT_OPERATIONS } from '@/config/productApi'
import {
  clearAuthenticatedSession,
  setStoredMuid,
  setStoredRequestedPath
} from '@/config/sourceStorage'

export const useSessionAuth = () => {
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { appendProductParams, getReturnUrl } = useProductConfig()
  const { apiRequest } = useFetch()

  const isHomeRoute = normalizePath(pathname) === normalizePath(ROUTES.HOME)
  const fullPath = `${pathname}${search || ''}`
  const onAuthRoute = isAuthRoute(pathname)
  const shouldSaveRedirect = shouldSaveAuthRedirect(pathname)
  const shouldRedirectToLogin = shouldSaveRedirect || isHomeRoute

  const isRedirecting = !loading && (isAuthenticated ? onAuthRoute : shouldRedirectToLogin)

  const loginUser = useCallback(muid => {
    if (muid) setStoredMuid(muid)
    setIsAuthenticated(true)
    setLoading(false)
  }, [])

  const checkAuthSession = useCallback(() => {
    setLoading(true)

    apiRequest(
      PRODUCT_OPERATIONS.AUTHENTICATE,
      {},
      {
        onSuccess: result => {
          if (result?.muid) setStoredMuid(result.muid)

          setIsAuthenticated(Boolean(result?.isActive))
          setLoading(false)
        },
        onError: () => {
          clearAuthenticatedSession()
          setIsAuthenticated(false)
          setLoading(false)
        }
      }
    )
  }, [apiRequest])

  useEffect(() => {
    checkAuthSession()
  }, [checkAuthSession])

  useEffect(() => {
    if (loading) return

    if (!isAuthenticated) {
      if (shouldRedirectToLogin) {
        if (shouldSaveRedirect) setStoredRequestedPath(fullPath)
        navigate(`${ROUTES.LOGIN}?${appendProductParams().toString()}`, { replace: true })
      }
      return
    }

    if (onAuthRoute) {
      setStoredRequestedPath(null)
      applyRedirect(resolvePostLoginRedirect(getReturnUrl), navigate)
    }
  }, [
    appendProductParams,
    fullPath,
    getReturnUrl,
    isAuthenticated,
    loading,
    navigate,
    onAuthRoute,
    shouldRedirectToLogin,
    shouldSaveRedirect
  ])

  return {
    isAuthenticated,
    loading,
    isRedirecting,
    checkAuthSession,
    loginUser
  }
}
